import React, { useEffect, useState } from "react";
import { api, fmtError, ROLE_LABELS, fmtDate } from "@/lib/apiClient";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLES = ["client_servicing", "execution", "execution_head", "delivery", "delivery_head", "reporting", "accounts", "client_user", "super_admin"];

export default function Users() {
    const [list, setList] = useState([]);
    const [clients, setClients] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ email: "", name: "", role: "execution", password: "", client_org_id: "", phone: "" });

    const load = async () => {
        try {
            const [u, c] = await Promise.all([api.get("/users"), api.get("/clients")]);
            setList(u.data);
            setClients(c.data);
        } catch (e) {}
    };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault();
        try {
            const payload = { ...form };
            if (form.role !== "client_user") delete payload.client_org_id;
            await api.post("/users", payload);
            toast.success("User added");
            setOpen(false);
            setForm({ email: "", name: "", role: "execution", password: "", client_org_id: "", phone: "" });
            load();
        } catch (e) { toast.error(fmtError(e.response?.data?.detail) || e.message); }
    };

    return (
        <div className="space-y-6">
            <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="overline mb-1">// Team</div>
                    <h1 className="font-display text-4xl tracking-tighter">Users & Roles</h1>
                    <p className="text-slate-500 mt-2 text-sm">{list.length} users across departments.</p>
                </div>
                <Button onClick={() => setOpen(true)} data-testid="add-user-button" className="rounded-none bg-slate-900 hover:bg-slate-800 text-white gap-2">
                    <Plus size={14} weight="bold" /> Add User
                </Button>
            </header>

            <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="users-table">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                        <tr>
                            <th className="text-left px-4 py-3 overline">Name</th>
                            <th className="text-left px-4 py-3 overline">Email</th>
                            <th className="text-left px-4 py-3 overline">Phone</th>
                            <th className="text-left px-4 py-3 overline">Role</th>
                            <th className="text-left px-4 py-3 overline">Created</th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.map((u) => (
                            <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                                <td className="px-4 py-3 flex items-center gap-3">
                                    <span className="w-8 h-8 bg-slate-900 text-white flex items-center justify-center font-mono text-xs">{u.name?.[0]}</span>
                                    <span className="font-medium">{u.name}</span>
                                </td>
                                <td className="px-4 py-3 font-mono text-xs text-slate-600">{u.email}</td>
                                <td className="px-4 py-3 font-mono text-xs text-slate-600">
                                    {u.phone ? (
                                        <span>{u.phone}</span>
                                    ) : (
                                        <span className="text-slate-400">—</span>
                                    )}
                                </td>
                                <td className="px-4 py-3"><span className="font-mono text-[10px] uppercase tracking-wider bg-slate-100 px-2 py-0.5">{ROLE_LABELS[u.role]}</span></td>
                                <td className="px-4 py-3 font-mono text-xs text-slate-500">{fmtDate(u.created_at)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {open && (
                <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-start justify-center p-4" data-testid="add-user-modal">
                    <form onSubmit={submit} className="bg-white border border-slate-200 max-w-md w-full mt-12">
                        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                            <h3 className="font-display text-lg">New User</h3>
                            <button type="button" onClick={() => setOpen(false)} className="text-2xl leading-none text-slate-500">×</button>
                        </div>
                        <div className="p-6 space-y-4">
                            <Field label="Name"><Input data-testid="user-form-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-none border-slate-300" /></Field>
                            <Field label="Email"><Input data-testid="user-form-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-none border-slate-300 font-mono" /></Field>
                            <Field label="Role">
                                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                                    <SelectTrigger data-testid="user-form-role" className="rounded-none border-slate-300"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </Field>
                            {form.role === "client_user" && (
                                <Field label="Client organization">
                                    <Select value={form.client_org_id} onValueChange={(v) => setForm({ ...form, client_org_id: v })}>
                                        <SelectTrigger className="rounded-none border-slate-300"><SelectValue placeholder="Select client" /></SelectTrigger>
                                        <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                                    </Select>
                                </Field>
                            )}
                            <Field label="Phone (E.164 — used for WhatsApp alerts)">
                                <Input
                                    data-testid="user-form-phone"
                                    type="tel"
                                    value={form.phone}
                                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                    placeholder="+14155551234"
                                    className="rounded-none border-slate-300 font-mono"
                                />
                            </Field>
                            <Field label="Initial password"><Input data-testid="user-form-password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="rounded-none border-slate-300 font-mono" /></Field>
                        </div>
                        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
                            <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none border-slate-300">Cancel</Button>
                            <Button type="submit" data-testid="user-form-submit" className="rounded-none bg-slate-900 hover:bg-slate-800 text-white">Add user</Button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="overline mb-1.5 block">{label}</Label>
            {children}
        </div>
    );
}
